import { type LogoComponent, type LogoProps } from '../registry';

interface ImageLogoOptions {
  /** Emitted asset URL — `import src from '../assets/<handle>.png'`. */
  src: string;
  /** Intrinsic pixel width — reserves layout before the file loads. */
  width: number;
  /** Intrinsic pixel height. */
  height: number;
  /** Accessible name, used only when a call site sets `ariaHidden={false}`. */
  alt: string;
  /**
   * Corner radius in *artwork* pixels, applied only when the caller passes
   * `rounded`. Defaults to 8% of the short edge, matching the `rx` the SVG
   * logos use on their plates (virtualclubhousegolf 48/649, DRC 100/1240).
   */
  radius?: number;
}

/**
 * First-party raster logo: wraps an imported image asset in a `LogoProps`
 * component so it slots into the tenant maps exactly like an SVG component.
 *
 * This replaces the two legacy raster patterns — base64 data URIs inlined in
 * the module (692KB of source for one logo) and rasters wrapped in
 * `<svg><image>` — both of which ship the pixels inside the JS bundle, +33%
 * inflated by the encoding and uncacheable. An imported asset is emitted as a
 * hashed file by the consuming app's bundler instead.
 *
 * `viewBox` is deliberately inert — images are pre-cropped in the asset.
 */
export function imageLogo({ src, width, height, alt, radius }: ImageLogoOptions): LogoComponent {
  // A percentage radius, not a px one: `border-radius: 12px` on an <img> keeps
  // its 12px at every render size, so a plate that looks right in a header
  // turns into a pill on a splash. Percentages resolve against the rendered
  // box, so the corner stays proportional to the artwork at any size.
  //
  // The pair syntax (`x% / y%`) is what keeps the corner circular rather than
  // elliptical on non-square artwork: each axis is the same artwork-pixel
  // radius expressed against that axis's own length.
  const r = radius ?? Math.round(Math.min(width, height) * 0.08);
  const borderRadius = `${(r / width) * 100}% / ${(r / height) * 100}%`;

  return function ImageLogo({
    width: renderWidth = width,
    height: renderHeight = height,
    className = 'h-auto max-w-full',
    ariaHidden = true,
    rounded = false,
  }: LogoProps) {
    return (
      <img
        src={src}
        alt={ariaHidden ? '' : alt}
        width={renderWidth}
        height={renderHeight}
        className={className}
        aria-hidden={ariaHidden}
        style={rounded ? { borderRadius } : undefined}
      />
    );
  };
}
